'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Clock, MapPin, Calendar } from 'lucide-react';
import { getUpcomingFixtures, getFlag, type LiveFixture } from '@/lib/api-football';
import { useLanguage } from '@/contexts/LanguageContext';

// World Cup 2026 preview fixtures shown when API returns no upcoming fixtures yet
const WC2026_PREVIEWS = [
  { round: 'Group Stage', date: 'Jun 11', home: 'Mexico', homeFlag: '🇲🇽', away: 'USA', awayFlag: '🇺🇸', venue: 'SoFi Stadium, Los Angeles' },
  { round: 'Group Stage', date: 'Jun 12', home: 'Brazil', homeFlag: '🇧🇷', away: 'Argentina', awayFlag: '🇦🇷', venue: 'MetLife Stadium, New York' },
  { round: 'Group Stage', date: 'Jun 13', home: 'France', homeFlag: '🇫🇷', away: 'Germany', awayFlag: '🇩🇪', venue: 'AT&T Stadium, Dallas' },
  { round: 'Group Stage', date: 'Jun 14', home: 'Spain', homeFlag: '🇪🇸', away: 'England', awayFlag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', venue: 'Rose Bowl, Pasadena' },
];

export default function UpcomingFixtures() {
  const [fixtures, setFixtures] = useState<LiveFixture[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  const fetchFixtures = useCallback(async () => {
    try {
      const data = await getUpcomingFixtures(8);
      setFixtures(data ?? []);
    } catch {
      setFixtures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFixtures();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchFixtures, 60_000);
    return () => clearInterval(interval);
  }, [fetchFixtures]);

  if (loading) {
    return (
      <div>
        <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Clock size={16} className="text-slate-400" />
          {t.dashboard.upcoming}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="rounded-xl animate-pulse" style={{ background: 'hsl(222 35% 11%)', height: 160 }} />
          ))}
        </div>
      </div>
    );
  }

  if (fixtures.length === 0) {
    return (
      <div>
        <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Clock size={16} className="text-slate-400" />
          {t.dashboard.upcoming}
        </h2>
        {/* World Cup 2026 Preview Cards when no fixtures from API */}
        <div className="space-y-3">
          <div
            className="rounded-xl p-4 flex items-center gap-3"
            style={{ background: 'hsl(189 94% 51% / 0.05)', border: '1px solid hsl(189 94% 51% / 0.15)' }}
          >
            <Calendar size={14} style={{ color: 'hsl(189 94% 65%)' }} className="flex-shrink-0" />
            <p className="text-sm font-medium" style={{ color: 'hsl(189 94% 65%)' }}>
              {t.dashboard.worldCupSchedule}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-4 gap-3">
            {WC2026_PREVIEWS.map((preview, idx) => (
              <div
                key={idx}
                className="rounded-xl p-4"
                style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-slate-500">{preview.round}</span>
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: 'hsl(189 94% 51% / 0.08)', color: 'hsl(189 94% 65%)' }}
                  >
                    {preview.date}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-col items-center gap-1 flex-1">
                    <span className="text-2xl">{preview.homeFlag}</span>
                    <span className="text-xs font-semibold text-slate-400 text-center">{preview.home}</span>
                  </div>
                  <div className="text-center">
                    <span className="text-sm font-bold text-slate-500">VS</span>
                  </div>
                  <div className="flex flex-col items-center gap-1 flex-1">
                    <span className="text-2xl">{preview.awayFlag}</span>
                    <span className="text-xs font-semibold text-slate-400 text-center">{preview.away}</span>
                  </div>
                </div>
                {preview.venue && (
                  <div className="mt-3 flex items-center gap-1 text-xs text-slate-600">
                    <MapPin size={10} />
                    <span className="truncate">{preview.venue}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
        <Clock size={16} className="text-slate-400" />
        {t.dashboard.upcoming}
        <span className="text-xs font-medium px-2 py-0.5 rounded-full text-slate-400" style={{ background: 'hsl(222 25% 18%)' }}>
          {fixtures.length}
        </span>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-4 gap-3">
        {fixtures.map((fix) => {
          const kickoffDate = new Date(fix.fixture.date);
          const kickoffStr = kickoffDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          const dateStr = kickoffDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
          const now = Date.now();
          const diffMs = kickoffDate.getTime() - now;
          const diffMin = Math.max(0, Math.round(diffMs / 60000));

          return (
            <Link key={fix.fixture.id} href={`/game-detail?id=${fix.fixture.id}`}>
              <div
                className="rounded-xl p-4 card-hover cursor-pointer group transition-all duration-200"
                style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 15%)' }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-slate-500">{fix.league.round}</span>
                  <div className="flex flex-col items-end gap-0.5">
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: 'hsl(222 25% 18%)', color: 'hsl(222 15% 60%)' }}
                    >
                      {kickoffStr}
                    </span>
                    <span className="text-xs text-slate-600">{dateStr}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-col items-center gap-1 flex-1">
                    {fix.teams.home.logo ? (
                      <img src={fix.teams.home.logo} alt={fix.teams.home.name} className="w-7 h-7 object-contain" />
                    ) : (
                      <span className="text-xl">{getFlag(fix.teams.home.name)}</span>
                    )}
                    <span className="text-xs font-semibold text-slate-400 text-center truncate w-full">
                      {fix.teams.home.code ?? fix.teams.home.name.slice(0, 3).toUpperCase()}
                    </span>
                  </div>
                  <div className="text-center">
                    <span className="text-sm font-bold text-slate-500">{t.common.vs.toUpperCase()}</span>
                  </div>
                  <div className="flex flex-col items-center gap-1 flex-1">
                    {fix.teams.away.logo ? (
                      <img src={fix.teams.away.logo} alt={fix.teams.away.name} className="w-7 h-7 object-contain" />
                    ) : (
                      <span className="text-xl">{getFlag(fix.teams.away.name)}</span>
                    )}
                    <span className="text-xs font-semibold text-slate-400 text-center truncate w-full">
                      {fix.teams.away.code ?? fix.teams.away.name.slice(0, 3).toUpperCase()}
                    </span>
                  </div>
                </div>

                {fix.fixture.venue.name && (
                  <div className="mt-3 flex items-center gap-1 text-xs text-slate-600">
                    <MapPin size={10} />
                    <span className="truncate">{fix.fixture.venue.name}{fix.fixture.venue.city ? `, ${fix.fixture.venue.city}` : ''}</span>
                  </div>
                )}

                {diffMin > 0 && (
                  <div className="mt-2 flex items-center justify-between">
                    <div
                      className="text-xs font-medium px-2 py-1 rounded-full"
                      style={{ background: 'hsl(25 95% 53% / 0.08)', color: 'hsl(25 95% 65%)' }}
                    >
                      {t.dashboard.inTime} {diffMin < 60 ? `${diffMin}m` : `${Math.floor(diffMin / 60)}h ${diffMin % 60}m`}
                    </div>
                    <span className="text-xs text-slate-600 group-hover:text-cyan-400 transition-colors">{t.dashboard.preview}</span>
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
